import { useMemo } from 'react';

// material-ui
import Typography from '@mui/material/Typography';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import CircularProgress from '@mui/material/CircularProgress';

// project import
import NavGroup from './NavGroup';
import { refreshUserContext, useUserContext, useUserContextRequestState } from 'services/userContextStore';
import {
  ApartmentOutlined,
  AppstoreOutlined,
  BarChartOutlined,
  BookOutlined,
  ContainerOutlined,
  DotChartOutlined,
  FileTextOutlined,
  FolderOpenOutlined,
  FundProjectionScreenOutlined,
  HomeOutlined,
  PlusCircleOutlined,
  ProfileOutlined,
  RocketOutlined,
  TeamOutlined
} from '@ant-design/icons';

const ICONS = {
  ApartmentOutlined,
  AppstoreOutlined,
  BarChartOutlined,
  BookOutlined,
  ContainerOutlined,
  DotChartOutlined,
  FileTextOutlined,
  FolderOpenOutlined,
  FundProjectionScreenOutlined,
  HomeOutlined,
  PlusCircleOutlined,
  ProfileOutlined,
  RocketOutlined,
  TeamOutlined
};

const DEFAULT_ICON = AppstoreOutlined;

/**
 * 将后端 coop_menus 中立结构适配成当前导航组件使用的 Mantis menu shape。
 * 后端只传 icon 名称，前端通过白名单映射组件，避免执行任意组件/HTML。
 */
function toNavigationItem(menu) {
  const children = Array.isArray(menu.children) ? menu.children.map(toNavigationItem) : [];
  const Icon = ICONS[menu.icon] || DEFAULT_ICON;
  const isGroup = Number(menu.node_type) === 1 || children.length > 0;

  if (isGroup) {
    return {
      id: String(menu.code || menu.id),
      title: menu.name,
      type: 'collapse',
      icon: Icon,
      children
    };
  }

  return {
    id: String(menu.code || menu.id),
    title: menu.name,
    type: 'item',
    url: menu.path,
    icon: Icon
  };
}

function buildNavigation(menus) {
  const children = [
    {
      id: 'home',
      title: '首页',
      type: 'item',
      url: '/',
      icon: HomeOutlined
    },
    ...(Array.isArray(menus) ? menus.map(toNavigationItem) : [])
  ];
  return [
    {
      id: 'coop-menu-root',
      title: '灵犀',
      type: 'group',
      children
    }
  ];
}

// ==============================|| DRAWER CONTENT - NAVIGATION ||============================== //

export default function Navigation() {
  const userContext = useUserContext();
  const { loading, error, loaded } = useUserContextRequestState();
  const navGroups = useMemo(() => buildNavigation(userContext?.coop_menus), [userContext?.coop_menus]);

  // 有缓存时始终优先展示缓存菜单，刷新失败也不能阻塞整个导航；无缓存时才展示请求状态。
  if (!userContext && (loading || (!loaded && !error))) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', pt: 4 }}>
        <CircularProgress size={22} />
      </Box>
    );
  }

  if (!userContext && error) {
    return (
      <Box sx={{ px: 2, pt: 3, textAlign: 'center' }}>
        <Typography variant="body2" color="error.main">
          菜单加载失败，请刷新重试
        </Typography>
        <Button size="small" sx={{ mt: 1 }} onClick={() => refreshUserContext().catch(() => {})} disabled={loading}>
          重新加载
        </Button>
      </Box>
    );
  }

  if (navGroups[0].children.length === 0) {
    return (
      <Box sx={{ px: 2, pt: 3 }}>
        <Typography variant="body2" color="text.secondary" align="center">
          暂无可显示菜单
        </Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ pt: 2 }}>
      {navGroups.map((item) => (
        <NavGroup key={item.id} item={item} />
      ))}
    </Box>
  );
}
